import { ApolloError } from '@apollo/client';
import { webApiClient } from '../authentication';
import { CheckoutOffer } from './offer';

/** What web puts on a purchase it rejects because the account's email has already used the adoption offer. */
export const GWA_PROMO_ALREADY_REDEEMED_CODE = 'gwa_promo_already_redeemed';

/** The check holds the whole checkout on a loading screen, so a hung request must not be able to. */
const CHECK_TIMEOUT_MS = 5000;

/**
 * Whether a purchase failed because the account's email has already used the adoption offer.
 *
 * Matched on the code web attaches to the error rather than on its message, so a rewording on the server
 * cannot silently turn this back into a dead end.
 */
export function isGwaPromoAlreadyRedeemed(error: unknown) {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.some((graphQLError) => graphQLError.extensions?.errorCode === GWA_PROMO_ALREADY_REDEEMED_CODE)
  );
}

/**
 * Whether the account behind `email` has already used this rescue offer, asked before the shopper
 * enters a card so they do not find out only when the purchase is rejected.
 *
 * Only a rescue offer is limited, and web answers only for one that carries its signed entitlement, so
 * the generic registration credit asks nothing. A check that cannot be answered counts as "not used":
 * the purchase is rejected for the same reason on the server, and `isGwaPromoAlreadyRedeemed` catches that.
 */
export async function hasRedeemedGwaOffer(email: string, offer: CheckoutOffer) {
  if (!offer.entitlement) {
    return false;
  }

  try {
    const { data } = await webApiClient.post(
      '/api/ecommerce/check-email',
      { email, couponCode: offer.couponCode, gwaToken: offer.entitlement },
      { timeout: CHECK_TIMEOUT_MS },
    );
    return data?.gwaPromoRedeemed === true;
  } catch {
    return false;
  }
}
